import type { ChatConversationSummary } from '@/api/comms/chat.api';

/**
 * Presentation helpers for the Messages surface (Stream E, ADR-027 §7.2).
 * Pure and locale-aware — timestamps arrive as ISO strings from the chat
 * API and are rendered relative-to-now (list rows) or as clock/day labels
 * (thread rows + separators).
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Compact relative age: "now", "5m", "3h", "2d", then a short date. */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = now - then;
  if (diff < MINUTE) return 'now';
  if (diff < HOUR) return `${String(Math.floor(diff / MINUTE))}m`;
  if (diff < DAY) return `${String(Math.floor(diff / HOUR))}h`;
  if (diff < 7 * DAY) return `${String(Math.floor(diff / DAY))}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Clock time for a message row, e.g. "2:05 PM". */
export function formatClockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Day-separator label: "Today", "Yesterday", or a full weekday + date. */
export function formatDayLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const startOf = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / DAY);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

/** True when two ISO timestamps fall on different calendar days (or the first is missing). */
export function isNewDay(prevIso: string | null, iso: string): boolean {
  if (!prevIso) return true;
  const a = new Date(prevIso);
  const b = new Date(iso);
  return a.getFullYear() !== b.getFullYear() || a.getMonth() !== b.getMonth() || a.getDate() !== b.getDate();
}

/**
 * The conversation's human name from the viewer's perspective: a group's
 * title (or its members' first names) or, for a DM, the other person's
 * full name.
 */
export function conversationDisplayName(
  conversation: Pick<ChatConversationSummary, 'isGroup' | 'title' | 'participants'>,
  myMembershipId: string | null,
): string {
  const others = conversation.participants.filter((p) => p.membershipId !== myMembershipId);
  if (conversation.isGroup) {
    if (conversation.title && conversation.title.trim() !== '') return conversation.title;
    const source = others.length > 0 ? others : conversation.participants;
    const names = source.map((p) => p.firstName).filter((n) => n.trim() !== '');
    return names.length > 0 ? names.join(', ') : 'Group conversation';
  }
  const other = others[0] ?? conversation.participants[0];
  return other ? `${other.firstName} ${other.lastName}`.trim() : 'Conversation';
}

/** Sender label for a thread row; departed members render as "Former teammate". */
export function senderName(
  participants: ChatConversationSummary['participants'],
  senderMembershipId: string | null,
): string {
  if (!senderMembershipId) return 'Former teammate';
  const p = participants.find((x) => x.membershipId === senderMembershipId);
  return p ? `${p.firstName} ${p.lastName}`.trim() : 'Former teammate';
}
